<template>
  <section :class="[isClosed, postContextClass, {'start-template' : fromStartButtonTemplate}]" class="create">
    <div class="preloader-container">
      <preloader v-if="saving && uploadingAttachments"
                 :attachmentsLoaded='uploadedAttachments'
                 :attachmentsLength='attachments.length'
                 mode='animate'
                 :background="true" />
      <attachments ref="attachments"
                   v-model="attachments"
                   :context="postContext"
                   v-bind="{saving, post, carouselPosition}"
                   @setOrToggleText="setOrToggleText"
                   :seenText="seenText"
                   @edited="edited = true" />
      <post-body id="post-body"
                 ref="text"
                 :class="{'post-body': attachments.length > 0}"
                 v-model="body"
                 :maxWords="maxWords"
                 :placeholder="placeholder"
                 :isPost="true"
                 @input="onChange" />
    </div>
    <template v-if="seenText || attachments.length || postContext == `inline-update`">
      <div class="error help has-text-danger">
        <div class="error help is-danger"
             v-if="error"> * {{error}}</div>
        <div v-if="hasCaptionError || error"
             class="caption-error help is-danger">* {{ $t('projectPage.noTextError') }}</div>
      </div>
    </template>
    <div v-if="error" class="error help has-text-danger"> * {{error}}</div>
    <div class="button-container is-flex">
      <div class="cancel-and-save"
           :class="{ 'saving-right-align' : saving}">
        <button v-if="!saving"
             @click="cancel"
             tabindex="0"
             class="button cancel is-outlined is-small is-primary is-uppercase"
             qa="cancel-button"> {{ $t("cancel") }} </button>
        <button class="button save is-small is-primary is-uppercase"
                tabindex="0"
                @click="saveAndClose"
                :class="{'disabled' : saving || !canSave}"
                :disabled="saving || !canSave">
          <span v-if="!saving" qa="save-button">
            {{label}}
          </span>
          <span v-if="saving && uploadingAttachments">{{ $t('projectPage.savingAttachment2') }} {{uploadedAttachments + 1}} {{ $t('projectPage.of') }} {{attachments.length}}</span>
          <span v-if="saving && !uploadingAttachments">{{ $t('projectPage.saving') }}</span>
          <progress v-if="saving && uploadingAttachments"
                    class="progress is-primary saving-text"
                    :value="monitor.progress" max="100" />
        </button>
      </div>
      <div v-if="showDeleteSection" class="delete-button-container">
        <button class="button delete-button is-small is-danger is-uppercase" @click="tryDelete">
            <span class="mr-2">
              <i class="fal fa-trash-alt"/>
            </span>
          <label v-if="isMobileViewport" class="edit-title">Delete</label>
          <label v-else class="edit-title">Delete Section</label>
        </button>
      </div>
      <modal-custom
          v-if="showDeleteModal"
          :hasFooter="true"
          :title="$t('projectPage.confirmDeleteSection')" :text="$t('projectPage.deleteSection')"
          :successText="$t('projectPage.yesDelete')" :cancelText="$t('cancel')"
          @modalSuccess="deleteSection" @modalCancel="showDeleteModal = false">
      </modal-custom>
      <modal-custom
          v-if="showCannotDeleteModal"
          :hasFooter="true"
          :title="$t('projectPage.unableDeleteSection')" :text="$t('projectPage.atLeastOneSection')"
          :successText="$t('ok')"
          @modalSuccess="showCannotDeleteModal = false">
      </modal-custom>
      <modal-custom
          v-if="showUnauthorizedDeleteModal"
          :hasFooter="true"
          :title="$t('projectPage.unableDeleteSection')" :text="$t('projectPage.unauthorizedDeleteSection')"
          :successText="$t('ok')"
          @modalSuccess="showUnauthorizedDeleteModal = false">
      </modal-custom>
    </div>
  </section>
</template>

<script>
import PostBody from '@/components/Inputs/QuillEditor'
import Preloader from '@/components/Controls/Preloader'
import base from '../mixins/base'
import save from '../mixins/save'
import i18n from '@/i18n'
import userAgent from '@/libs/user-agent'
import state from '../../../SectionProjectView/state'
import ModalCustom from '@/components/Modals/Custom'
import breakpoints from '@/libs/breakpoints'

const dispatch = {
  create: 'createPost',
  update: 'updatePost',
  get: 'getPost',
  addAttachments: 'addPostAttachments',
  updateAttachments: 'updatePostAttachment',
  deleteAttachments: 'deletePostAttachment'
}

export default {

  props: ['project', 'projectType', 'post', 'update', 'context',
    'forceFocus', 'carouselPosition', 'projectTitle', 'projectTags',
    'projectCategories', 'projectIsPrivate', 'hasCaptionError', 'hasTitleError', 'isJustForSaving'],

  components: {
    PostBody,
    Preloader,
    ModalCustom
  },

  mixins: [base, save],

  data: function () {
    return {
      dispatch,
      wordCount: 0,
      isMounted: false,
      hasNonCharContent: false,
      hasProfanity: false,
      showDeleteModal: false,
      showCannotDeleteModal: false,
      showUnauthorizedDeleteModal: false
    }
  },
  watch: {
    edited: function () {
      if (this.edited && this.post !== undefined) {
        this.$store.dispatch('setEdited', { id: this.post.id, edited: true })
      }
    }
  },

  computed: {
    entity: function () {
      return this.post || null
    },
    label: function () {
      if (this.postContext === 'inline-update') {
        return i18n.t('projectPage.save')
      } else {
        return i18n.t('projectPage.post')
      }
    },
    placeholder: function () {
      if (this.projectType === 'story') {
        return 'Start a new section'
      }
      if (this.postContext === 'post') {
        return 'Start your first section'
      }
      if (this.postContext === 'inline') {
        return i18n.t('projectPage.updateProject')
      }
      if (this.postContext === 'inline-update') {
        return ''
      }
    },
    canSave: function () {
      if (this.maxWords > 0 && this.wordCount > this.maxWords) {
        return false
      }
      if (this.wordCount === 0 && this.numAttachments < 1 && !this.hasNonCharContent) {
        return false
      }
      if (this.bodyLength > 2 || this.body.replace(/([>#*1.])+/g, '') !== '') {
        return true
      }
      return this.numAttachments > 0 && this.projectID
    },
    projectID: function () {
      if (this.project) {
        return this.project.id
      }

      if (this.post) {
        return this.post.projectID
      }
    },
    createKey: function () {
      return this._uicreateKey(this.projectID, this.post)
    },
    annotateKey: function () {
      return this.project ? `postFrom.${this.project.id}` : null
    },
    isClosed: function () {
      return !this.seenText && !this.uploadingAttachments ? 'closed' : 'open'
    },
    canDelete: function () {
      return !this.saving && this.postContext === 'inline-update' && this.entity
    },
    fromStartButtonTemplate: function () {
      return this.$route && (!!this.$route.params.startButton || !!this.$route.params.fromTemplate)
    },
    isMobile: function () {
      return userAgent.isApp() || userAgent.isIOS() || userAgent.isAndroid()
    },
    isMobileViewport: function () {
      return breakpoints.isMobile()
    },
    maxWords: function () {
      return this.post?.maxWords ? this.post.maxWords : 0
    },
    numberOfPosts: function () {
      return this._get(state.posts.total, false)
    },
    isSectionDeletable: function () {
      return this.isAdmin || this.projectRole === 'anonAdmin' || this.projectRole === 'anonCoordinator' || (this.post && this.post.postFeatures.canDelete)
    },
    doesSectionPostExist: function () {
      return !(this.post == null)
    },
    showDeleteSection: function () {
      return (!this.saving && this.doesSectionPostExist && this.post.postFeatures.canEdit && !this.$isOhioScience) || (this.$isOhioScience && this.post.project.isTemplate)
    }
  },

  mounted: function () {
    if (this.update) {
      this.priority = this.entity.priority
    }
    if (this.$refs.text) {
      this.$refs.text.setEncodedContents(this.body)
    }
    if (this.forceFocus && !this.isMobile) {
      this.$nextTick(() => {
        this.$refs.text.focusAtEnd()
      })
    }
    this.wordCount = this.$refs.text.wordCount
    this.isMounted = true
  },

  methods: {
    onChange: function () {
      if (!this.isMounted) {
        return
      }
      if (this.isJustForSaving) {
        return
      }
      this.edited = true
      this.wordCount = this.$refs.text.wordCount
      this.hasNonCharContent = this.$refs.text.hasNonCharContent
      this.hasProfanity = this.$refs.text?.hasProfanity()
    },
    checkCanSave: function () {
      this.error = ''
      const saveOk = this.canSave
      if (!saveOk) {
        this.error = 'Please add text before saving'
        this.scrollIntoView()
      }
      return saveOk
    },
    createParams: function () {
      const projectCategories = this.projectCategories ? this.projectCategories.map((category) => category.id) : []

      return {
        body: this.body,
        projectType: this.projectType,
        projectID: this.projectID,
        attachments: this.attachments,
        projectTitle: this.projectTitle,
        projectTags: this.projectTags,
        projectCategories,
        projectIsPrivate: this.projectIsPrivate
      }
    },

    updateParams: function () {
      return { id: this.post.id, body: this.body, priority: this.priority }
    },

    socketParams: function () {
      return ['project', this.projectID, 'post']
    },

    setOrToggleText: function (status) {
      this.seenText = status === undefined ? !this.seenText : status
      this.$emit('setSeenText', this.seenText)
      if (this.seenText && !this.isMobile) {
        this.$nextTick(() => { this.$refs.text.focusAtEnd() })
      }
    },

    scrollIntoView: function () {
      if (this.$route.query.t && this.update) {
        return
      }

      const attachments = this.$refs.attachments
      if (attachments) {
        const rect = attachments.$el.getBoundingClientRect()
        if (this.$route.fullPath === '/post') {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }

        window.scrollTo({
          top: (window.scrollY || window.pageYOffset) + rect.top,
          behavior: 'smooth'
        })
      }
    },
    cancel: function () {
      if (this.postContext === 'inline') {
        this.setOrToggleText(false)
        this.$emit('cancel')
      } else {
        this.$emit('cancel')
      }
      this.isCancelling = true
      if (!this.update) {
        this.body = ''
        this.attachments = []
      }
    },
    getStoreData: function () {
      return {
        projectID: this.projectID,
        commentID: null,
        body: this.body,
        attachments: this.attachments,
        uploadedAttachments: this.uploadedAttachments,
        seenText: this.seenText
      }
    },
    saveAndClose: async function () {
      if (this.hasProfanity) {
        const err = this.hasProfanity
        return err
      }
      await this.save()
      this.$emit('saveAndClose', this.isEdited)
    },
    tryDelete: function () {
      if (this.numberOfPosts > 1) {
        if (this.isSectionDeletable) {
          this.showDeleteModal = true
        } else {
          this.showUnauthorizedDeleteModal = true
        }
      } else {
        this.showCannotDeleteModal = true
      }
    },
    deleteSection: function () {
      const post = this.post
      this.$store.dispatch('deletePost', post).then(() => {
        this._set(state.posts.total, this.numberOfPosts - 1)
        this.showDeleteModal = false
        this._set(state.grid.modalVisible, false)
        this.$emit('close', true)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  // There are 5 types of postContext:
  //   create-post            Post page '/post'
  //   create-inline          Post from a project page
  //   create-inline-update   Edit a post in list view
  //   create-comment         Create + edit a comment
  //   create-comment-update  Edit a comment

  section {
    width: 100%;
  }
  .edit-title {
    cursor: pointer;
  }
  .delete-button-container {
    align-self: flex-end;
    margin: 10px 0 13px 10px;
  }
  .delete-button {
    width: fit-content;
  }
  .cancel-and-save {
    margin: 10px 10px 13px 0;
    button {
      width: 73px;
    }
    .save {
      margin-bottom: 0;
    }
    .disabled {
      pointer-events: none;
    }
    div {
      border-radius: 1px;
      font-size: .65rem;
      min-width: 72px;
      height: 30px;
    }
  }
  .saving-right-align {
    width: 100%;
    text-align: right;
  }

  .del {
    margin: 10px 0;
  }

  .create {

    .button-container {
      justify-content: flex-end;
    }

    &.create-inline {
      padding: 6.25rem 2.5rem 1rem 2.5rem;
      border-bottom: 20px solid #f2f2f2;

      @include mobile {
        padding: 7rem 2rem 1rem 2rem;
      }

      &.closed {
        @include mini-mobile {
          padding-bottom: 0.5rem;
        }
      }
      &.open {
        padding-bottom: 0.5rem;
      }
    }

    &.create-inline-update {
      background-color: white;
      padding: 0 2rem;
      @include mobile {
        padding: 0 1rem;
      }
      .button-container {
        justify-content: space-between;
      }
      .no-delete {
        justify-content: flex-end
      }
    }
  }

  .button {
    span {
      z-index: 2;
    }
    .progress {
      position: absolute;
      z-index: 1;
      opacity: 0.3;
      height: inherit;
      left: 0;
      top: 0;
      &::-webkit-progress-bar {
        background-color: white;
      }
    }
  }

  .create.create-inline-update.start-template {
    background-color: white;
    padding-bottom: 2rem;
  }

  .error {
    @include mobile {
      font-size: 0.75rem;
      bottom: 1rem;
    }
  }

  .preloader-container {
    position: relative
  }
</style>
